"use client";
import { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter — Count-Up auf Stat-Zahl, getriggert sobald sichtbar.
 *
 * A11y: feste Endzahl ist im aria-label hinterlegt; das visuelle <span>
 * ist aria-hidden, sodass Screenreader NICHT den schrittweisen Count
 * ansagen (das waere lautes Geraeusch). prefers-reduced-motion -> direkt
 * Endwert.
 */
export function AnimatedCounter({
  target,
  duration = 1600,
  suffix = "",
  className = "lp-stat-cinema-number",
}: {
  target: number;
  duration?: number;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const startedRef = useRef(false);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") {
      // SSR: Endwert direkt rendern, damit der Counter nie bei 0 ausgeliefert
      // wird (No-JS / Crawler / Hydration-Mismatch-Schutz).
      setValue(target);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;

    const prefersReduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // prefers-reduced-motion ODER fehlender IntersectionObserver -> SOFORT
    // Endwert (kein Count-Up, aber garantiert nicht bei 0 hängend).
    if (prefersReduce || typeof IntersectionObserver === "undefined") {
      setValue(target);
      return undefined;
    }

    let raf = 0;
    const runCountUp = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        setValue(Math.round(target * eased));
        if (t < 1) raf = requestAnimationFrame(tick);
        // Garantie: bei t===1 steht exakt der Endwert (eased===1).
      };
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !startedRef.current) {
            runCountUp();
            io.disconnect();
          }
        }
      },
      // threshold 0 + rootMargin: feuert sobald die Kachel den Viewport auch
      // nur beruehrt. Die hohe clamp(72-140px)-Zahl erreichte mit threshold
      // 0.3 auf kurzen Viewports nie 30% Sichtbarkeit und blieb bei 0 stehen.
      { threshold: 0, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(el);

    // Sicherheits-Fallback: falls der IO aus irgendeinem Grund nie feuert
    // (z.B. Element startet bereits im Viewport, aber Browser liefert keinen
    // initialen Callback, oder display-Quirks), nach 1.2s den Count-Up
    // erzwingen, damit die Kachel garantiert den Endwert erreicht.
    const fallback = window.setTimeout(() => {
      if (!startedRef.current) {
        runCountUp();
        io.disconnect();
      }
    }, 1200);

    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, duration]);

  return (
    <span
      ref={ref}
      className={className}
      aria-label={`${target}${suffix}`}
      role="text"
    >
      <span aria-hidden="true">
        {value}
        {suffix}
      </span>
    </span>
  );
}
